import { useCallback, useEffect, useRef, useState } from 'react'

import {
  PTA_BASELINE_INCOMPLETE_ERROR_MESSAGE,
  PTA_BASELINE_VERIFICATION_FAILED_MESSAGE,
  hasPersistedPtaBaselineConfirmation,
  isPtaBaselineCheckEligibleStatus,
  isPtaBaselineProgressComplete,
  resolvePtaBaselineFailureMessage,
  shouldShowPtaBaselineCompleteNotification
} from './ptaConsentBaselineCompleteAnnounce'
import { GetPatientOptions } from './PatientDetail.types'
import {
  AuthError,
  cancelConfirmPatientBaselineComplete,
  confirmPatientBaselineComplete
} from '../../../services/ApiService'
import {
  OrgAddressDto,
  PatientDetailsDto,
  ProgramCatalogDto
} from '../../../types/swaggerTypes'
import { isPatientDetailPostTrialAccess } from '../patientInitialsDisplay'

export interface UsePtaConsentBaselineCompleteAnnounceParams {
  patientId?: string
  physicianId?: string
  patient?: PatientDetailsDto
  programCatalog?: ProgramCatalogDto
  defaultShippingAddress?: OrgAddressDto | null
  instituteCountryCode?: string | null
  isLoading: boolean
  getPatient: (options?: GetPatientOptions) => void
  onProgressCheckFailed?: (message: string) => void
  /** Baseline not 100% complete (200 response with incomplete flag). */
  onProgressCheckIncomplete?: (message: string) => void
}

export interface UsePtaConsentBaselineCompleteAnnounceResult {
  showConsentBaselineCompleteNotification: boolean
  dismissConsentBaselineCompleteNotification: () => void
  confirmConsentBaselineComplete: () => Promise<void>
  isCheckingConsentBaselineProgress: boolean
  isConsentBaselineConfirmed: boolean
}

/**
 * CLOS-13424 / CLOS-13651 — PTA baseline completion via BFF orchestration.
 * Portal calls baseline-complete; BFF uses Participant Progress (CLOS-13645)
 * and persists edcCompletionFlag when the baseline visit is 100% complete.
 */
export function usePtaConsentBaselineCompleteAnnounce({
  patientId,
  physicianId,
  patient,
  programCatalog,
  defaultShippingAddress,
  instituteCountryCode,
  isLoading,
  getPatient,
  onProgressCheckFailed,
  onProgressCheckIncomplete
}: UsePtaConsentBaselineCompleteAnnounceParams): UsePtaConsentBaselineCompleteAnnounceResult {
  const [showNotification, setShowNotification] = useState(false)
  const [isCheckingProgress, setIsCheckingProgress] = useState(false)
  const [isConsentBaselineConfirmed, setIsConsentBaselineConfirmed] =
    useState(false)
  const progressRequestIdRef = useRef(0)

  const dismissConsentBaselineCompleteNotification = useCallback(() => {
    setShowNotification(false)
  }, [])

  useEffect(() => {
    progressRequestIdRef.current += 1
    cancelConfirmPatientBaselineComplete()
    setShowNotification(false)
    setIsConsentBaselineConfirmed(false)
    setIsCheckingProgress(false)
  }, [patientId, physicianId])

  // Seed confirmed state from persisted Patient Details (CLOS-13667 / reload).
  // Prefer patientBaselineCaptured; also accept a later order-lifecycle
  // status once consent is captured, since Oracle can advance status through the
  // order journey without echoing patientBaselineCaptured.
  // Only when the loaded patient matches the current route ids so stale props
  // during navigation do not re-confirm the previous patient.
  useEffect(() => {
    if (
      patient &&
      patientId &&
      String(patient.patientId) === String(patientId) &&
      hasPersistedPtaBaselineConfirmation(patient)
    ) {
      setIsConsentBaselineConfirmed(true)
    }
  }, [patient, patientId])

  const isPostTrialAccess = isPatientDetailPostTrialAccess(
    patient?.program?.status,
    programCatalog,
    defaultShippingAddress,
    instituteCountryCode
  )

  const confirmConsentBaselineComplete = useCallback(async () => {
    if (
      !patientId ||
      !physicianId ||
      !isPostTrialAccess ||
      isLoading ||
      !patient ||
      isCheckingProgress
    ) {
      return
    }

    const requestId = ++progressRequestIdRef.current
    setIsCheckingProgress(true)

    try {
      const { data: confirmation } = await confirmPatientBaselineComplete(
        patientId,
        physicianId
      )

      if (requestId !== progressRequestIdRef.current) {
        return
      }

      // Success only when BFF persisted edcCompletionFlag after baseline complete.
      if (
        confirmation.baselineCompletionPersisted === true &&
        shouldShowPtaBaselineCompleteNotification(
          patient,
          confirmation,
          isPostTrialAccess
        )
      ) {
        getPatient()
        setIsConsentBaselineConfirmed(true)
        setShowNotification(true)
        return
      }

      getPatient()
      setIsConsentBaselineConfirmed(false)

      // Only surface AC2 incomplete copy when the status is eligible for the
      // check and BFF reports baseline not complete — not when the patient has
      // already moved past baseline-check statuses.
      if (
        isPtaBaselineCheckEligibleStatus(patient) &&
        !isPtaBaselineProgressComplete(confirmation)
      ) {
        onProgressCheckIncomplete?.(PTA_BASELINE_INCOMPLETE_ERROR_MESSAGE)
      } else if (
        isPtaBaselineCheckEligibleStatus(patient) &&
        confirmation.baselineCompletionPersisted !== true
      ) {
        onProgressCheckFailed?.(PTA_BASELINE_VERIFICATION_FAILED_MESSAGE)
      }
    } catch (error) {
      if (
        error instanceof Error &&
        error.message === AuthError.RequestCancelled
      ) {
        return
      }

      if (requestId === progressRequestIdRef.current) {
        onProgressCheckFailed?.(resolvePtaBaselineFailureMessage(error))
      }
    } finally {
      if (requestId === progressRequestIdRef.current) {
        setIsCheckingProgress(false)
      }
    }
  }, [
    isCheckingProgress,
    isLoading,
    isPostTrialAccess,
    onProgressCheckFailed,
    onProgressCheckIncomplete,
    patient,
    patientId,
    physicianId,
    getPatient
  ])

  return {
    showConsentBaselineCompleteNotification: showNotification,
    dismissConsentBaselineCompleteNotification,
    confirmConsentBaselineComplete,
    isCheckingConsentBaselineProgress: isCheckingProgress,
    isConsentBaselineConfirmed
  }
}
